import { CitySelectionDialog } from './components/CitySelectionDialog'
import { ColumnVisibilityButton } from './components/ColumnVisibilityButton'
import { DatePicker } from './components/DatePicker'
import { TableToolbar } from './components/TableToolbar'
import { UsersPreview } from './components/UsersPreview'
import { useTableControls, TableColumn } from './hooks/useTableControls'
import {
  createOrganization as createOrg,
  getAllCities as fetchAllCities,
  getAllCityGroups as fetchAllCityGroups,
} from './services/organizationService'
import { organizationsTheme, emotionCache } from './theme/organizationsTheme'
import { City, CityGroup, User } from './types'
import { CacheProvider } from '@emotion/react'
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  LocationOn as LocationOnIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Card,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import React, { useState, useMemo, useEffect } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { withRouter } from 'react-router-dom'

interface Props {
  readonly history: { push: (path: string) => void }
}

const mapRoleToString = (role: User['role']): string => {
  if (role === 'Admin') return 'admin'
  if (role === 'Private User') return 'private_user'
  return 'user'
}

// eslint-disable-next-line complexity
const OrganizationCreate: React.FC<Props> = ({ history }) => {
  const intl = useIntl()
  const [orgName, setOrgName] = useState('')
  const [orgType, setOrgType] = useState('')
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [contact, setContact] = useState('')
  const [address1, setAddress1] = useState('')
  const [address2, setAddress2] = useState('')
  const [city, setCity] = useState('')
  const [country, setCountry] = useState('')
  const [region, setRegion] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [expiryDate, setExpiryDate] = useState('')
  const [billingNotes, setBillingNotes] = useState('')
  const [assignedCities, setAssignedCities] = useState<number[]>([])
  const [subscriptionType, setSubscriptionType] = useState('Demo')
  const [subscriptionStartDate, setSubscriptionStartDate] = useState('')
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  const getDayAfter = (dateStr: string): string => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    date.setDate(date.getDate() + 1)
    return date.toISOString().split('T')[0]
  }

  const handleStartDateChange = (date: string) => {
    setSubscriptionStartDate(date)
    // Clear expiry if it's now invalid (on or before new start date)
    if (date && expiryDate && new Date(date) >= new Date(expiryDate)) {
      setExpiryDate('')
    }
  }
  const [users, setUsers] = useState<User[]>([])
  const [cities, setCities] = useState<City[]>([])
  const [cityGroups, setCityGroups] = useState<CityGroup[]>([])

  useEffect(() => {
    fetchAllCities()
      .then((data) => setCities(data))
      .catch(() => {
        /* Ignored */
      })
    fetchAllCityGroups()
      .then((data) => setCityGroups(data))
      .catch(() => {
        /* Ignored */
      })
  }, [])
  const hasPrimaryContact = users.some((u) => u.isPrimaryContact)

  const isValid =
    orgName.trim() !== '' &&
    hasPrimaryContact &&
    city.trim() !== '' &&
    country.trim() !== '' &&
    region.trim() !== '' &&
    postalCode.trim() !== '' &&
    expiryDate !== '' &&
    subscriptionStartDate !== '' &&
    assignedCities.length > 0 &&
    users.length > 0

  const assignedCitiesData = cities.filter((c) => assignedCities.includes(c.id))

  const cityTableColumns: TableColumn[] = useMemo(
    () => [
      {
        id: 'name',
        label: intl.formatMessage({ id: 'organizations.cityTable.name' }),
        visible: true,
        sortable: true,
      },
      {
        id: 'state',
        label: intl.formatMessage({ id: 'organizations.cityTable.state' }),
        visible: true,
        sortable: true,
      },
      {
        id: 'country',
        label: intl.formatMessage({ id: 'organizations.cityTable.country' }),
        visible: true,
        sortable: true,
      },
    ],
    [intl]
  )

  const {
    page: cityPage,
    rowsPerPage: cityRowsPerPage,
    columns: cityColumns,
    visibleColumns: cityVisibleColumns,
    paginatedData: paginatedCities,
    sortBy: citySortBy,
    sortDirection: citySortDirection,
    handleChangePage: handleCityChangePage,
    handleChangeRowsPerPage: handleCityChangeRowsPerPage,
    toggleColumn: toggleCityColumn,
    handleSort: handleCitySort,
  } = useTableControls(assignedCitiesData, cityTableColumns)

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [saving, setSaving] = useState(false)

  const handleSave = () => {
    if (!isValid) return
    setSaving(true)
    createOrg({
      name: orgName,
      address1,
      address2,
      city,
      province: region,
      country,
      postalCode,
      orgType,
      subscriptionType,
      subscriptionStart: subscriptionStartDate,
      subscriptionExpiry: expiryDate,
      billingNotes,
      cityIds: assignedCities,
      users: users.map((u) => ({
        firstName: u.firstName,
        lastName: u.lastName,
        email: u.email,
        phone: u.phone,
        title: u.title,
        isPrimaryContact: u.isPrimaryContact,
        role: mapRoleToString(u.role),
      })),
    })
      .then(() => history.push('/organizations'))
      .catch(() => {
        setSaving(false)
      })
  }

  const handleSaveCities = (selectedCities: number[]) =>
    setAssignedCities(selectedCities)

  const missingRequirements: string[] = []
  if (orgName.trim() === '')
    missingRequirements.push(
      intl.formatMessage({ id: 'organizations.create.orgName' })
    )
  if (!hasPrimaryContact)
    missingRequirements.push(
      intl.formatMessage({ id: 'organizations.validation.primaryContact' })
    )
  if (city.trim() === '')
    missingRequirements.push(
      intl.formatMessage({ id: 'organizations.create.city' })
    )
  if (country.trim() === '')
    missingRequirements.push(
      intl.formatMessage({ id: 'organizations.create.country' })
    )
  if (region.trim() === '')
    missingRequirements.push(
      intl.formatMessage({ id: 'organizations.create.region' })
    )
  if (postalCode.trim() === '')
    missingRequirements.push(
      intl.formatMessage({ id: 'organizations.create.postalCode' })
    )
  if (subscriptionStartDate === '')
    missingRequirements.push(
      intl.formatMessage({ id: 'organizations.create.subscriptionStart' })
    )
  if (expiryDate === '')
    missingRequirements.push(
      intl.formatMessage({ id: 'organizations.create.subscriptionExpiry' })
    )
  if (assignedCities.length === 0)
    missingRequirements.push(
      intl.formatMessage({ id: 'organizations.validation.atLeastOneCity' })
    )
  if (users.length === 0)
    missingRequirements.push(
      intl.formatMessage({ id: 'organizations.validation.atLeastOneUser' })
    )

  return (
    <CacheProvider value={emotionCache}>
      <ThemeProvider theme={organizationsTheme}>
        <Box
          sx={{
            maxWidth: 1800,
            mx: 'auto',
            p: { xs: 2, sm: 3, md: 4 },
            pt: { xs: 10, md: 4 },
            color: '#fff',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              mb: 4,
              gap: 2,
            }}
          >
            <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => history.push('/organizations')}
                sx={{ mb: 2 }}
              >
                <FormattedMessage id="organizations.create.back" />
              </Button>
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}
              >
                <FormattedMessage id="organizations.create.title" />
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={!isValid}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <FormattedMessage id="organizations.create.save" />
            </Button>
          </Box>

          {/* Validation Alert */}
          {!isValid && (
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                <FormattedMessage id="organizations.create.validationMessage" />
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 3 }}>
                {missingRequirements.map((req, index) => (
                  <li key={index}>{req}</li>
                ))}
              </Box>
            </Alert>
          )}

          {/* Organization Details */}
          <Card sx={{ mb: 3, overflow: 'visible' }}>
            <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">
                <FormattedMessage id="organizations.create.details" />
              </Typography>
            </Box>
            <Box sx={{ p: 3, overflow: 'visible' }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={intl.formatMessage({
                      id: 'organizations.create.orgName',
                    })}
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    placeholder={intl.formatMessage({
                      id: 'organizations.create.orgNamePlaceholder',
                    })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>
                      {intl.formatMessage({
                        id: 'organizations.create.orgType',
                      })}
                    </InputLabel>
                    <Select
                      value={orgType}
                      onChange={(e) => setOrgType(e.target.value)}
                      label={intl.formatMessage({
                        id: 'organizations.create.orgType',
                      })}
                    >
                      <MenuItem value="">
                        {intl.formatMessage({
                          id: 'organizations.create.orgTypeSelect',
                        })}
                      </MenuItem>
                      <MenuItem value="Municipality">
                        {intl.formatMessage({
                          id: 'organizations.orgType.municipality',
                        })}
                      </MenuItem>
                      <MenuItem value="State Government">
                        {intl.formatMessage({
                          id: 'organizations.orgType.stateGovernment',
                        })}
                      </MenuItem>
                      <MenuItem value="Federal Government">
                        {intl.formatMessage({
                          id: 'organizations.orgType.federalGovernment',
                        })}
                      </MenuItem>
                      <MenuItem value="Non-profit/NGO">
                        {intl.formatMessage({
                          id: 'organizations.orgType.nonProfitNgo',
                        })}
                      </MenuItem>
                      <MenuItem value="Foundation">
                        {intl.formatMessage({
                          id: 'organizations.orgType.foundation',
                        })}
                      </MenuItem>
                      <MenuItem value="Private Sector">
                        {intl.formatMessage({
                          id: 'organizations.orgType.privateSector',
                        })}
                      </MenuItem>
                      <MenuItem value="Academic">
                        {intl.formatMessage({
                          id: 'organizations.orgType.academic',
                        })}
                      </MenuItem>
                      <MenuItem value="Municipal Association">
                        {intl.formatMessage({
                          id: 'organizations.orgType.municipalAssociation',
                        })}
                      </MenuItem>
                      <MenuItem value="Faith Organization">
                        {intl.formatMessage({
                          id: 'organizations.orgType.faithOrganization',
                        })}
                      </MenuItem>
                      <MenuItem value="Intergovernmental Organization">
                        {intl.formatMessage({
                          id: 'organizations.orgType.intergovernmentalOrganization',
                        })}
                      </MenuItem>
                      <MenuItem value="Other">
                        {intl.formatMessage({
                          id: 'organizations.orgType.other',
                        })}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={intl.formatMessage({
                      id: 'organizations.create.address1',
                    })}
                    value={address1}
                    onChange={(e) => setAddress1(e.target.value)}
                    placeholder={intl.formatMessage({
                      id: 'organizations.create.address1Placeholder',
                    })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={intl.formatMessage({
                      id: 'organizations.create.address2',
                    })}
                    value={address2}
                    onChange={(e) => setAddress2(e.target.value)}
                    placeholder={intl.formatMessage({
                      id: 'organizations.create.address2Placeholder',
                    })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={intl.formatMessage({
                      id: 'organizations.create.city',
                    })}
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder={intl.formatMessage({
                      id: 'organizations.create.cityPlaceholder',
                    })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={intl.formatMessage({
                      id: 'organizations.create.region',
                    })}
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    placeholder={intl.formatMessage({
                      id: 'organizations.create.regionPlaceholder',
                    })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={intl.formatMessage({
                      id: 'organizations.create.country',
                    })}
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder={intl.formatMessage({
                      id: 'organizations.create.countryPlaceholder',
                    })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={intl.formatMessage({
                      id: 'organizations.create.postalCode',
                    })}
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    placeholder={intl.formatMessage({
                      id: 'organizations.create.postalCodePlaceholder',
                    })}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>
          </Card>

          {/* Subscription Details */}
          <Card sx={{ mb: 3, overflow: 'visible' }}>
            <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">
                <FormattedMessage id="organizations.create.subscription" />
              </Typography>
            </Box>
            <Box sx={{ p: 3, overflow: 'visible' }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>
                      {intl.formatMessage({
                        id: 'organizations.create.subscriptionType',
                      })}
                    </InputLabel>
                    <Select
                      value={subscriptionType}
                      onChange={(e) => setSubscriptionType(e.target.value)}
                      label={intl.formatMessage({
                        id: 'organizations.create.subscriptionType',
                      })}
                    >
                      <MenuItem value="Demo">
                        {intl.formatMessage({
                          id: 'organizations.subscriptionType.demo',
                        })}
                      </MenuItem>
                      <MenuItem value="Pilot">
                        {intl.formatMessage({
                          id: 'organizations.subscriptionType.pilot',
                        })}
                      </MenuItem>
                      <MenuItem value="Custom">
                        {intl.formatMessage({
                          id: 'organizations.subscriptionType.custom',
                        })}
                      </MenuItem>
                      <MenuItem value="Canada100">
                        {intl.formatMessage({
                          id: 'organizations.subscriptionType.canada100',
                        })}
                      </MenuItem>
                      <MenuItem value="US1000">
                        {intl.formatMessage({
                          id: 'organizations.subscriptionType.us1000',
                        })}
                      </MenuItem>
                      <MenuItem value="Global1000">
                        {intl.formatMessage({
                          id: 'organizations.subscriptionType.global1000',
                        })}
                      </MenuItem>
                      <MenuItem value="Faith1000">
                        {intl.formatMessage({
                          id: 'organizations.subscriptionType.faith1000',
                        })}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    value={subscriptionStartDate}
                    onChange={handleStartDateChange}
                    placeholder={intl.formatMessage({
                      id: 'organizations.create.subscriptionStartPlaceholder',
                    })}
                    label={intl.formatMessage({
                      id: 'organizations.create.subscriptionStart',
                    })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    value={expiryDate}
                    onChange={setExpiryDate}
                    placeholder={intl.formatMessage({
                      id: 'organizations.create.subscriptionExpiryPlaceholder',
                    })}
                    label={intl.formatMessage({
                      id: 'organizations.create.subscriptionExpiry',
                    })}
                    min={getDayAfter(subscriptionStartDate)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={intl.formatMessage({
                      id: 'organizations.create.billingNotes',
                    })}
                    value={billingNotes}
                    onChange={(e) => setBillingNotes(e.target.value)}
                    placeholder={intl.formatMessage({
                      id: 'organizations.create.billingNotesPlaceholder',
                    })}
                    multiline
                    rows={4}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, bgcolor: 'background.default' }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      <FormattedMessage id="organizations.create.citiesAssigned" />
                    </Typography>
                    <Typography variant="h3">
                      {assignedCities.length}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Card>

          {/* Users */}
          <Box sx={{ mb: 3 }}>
            <UsersPreview
              users={users}
              setUsers={setUsers}
              onPrimaryContactChange={setContact}
            />
          </Box>

          {/* Cities */}
          <Card>
            <Box
              sx={{
                p: 3,
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6">
                <FormattedMessage id="organizations.create.assignedCities" />
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {assignedCities.length > 0 && (
                  <ColumnVisibilityButton
                    columns={cityColumns}
                    onToggleColumn={toggleCityColumn}
                  />
                )}
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<EditIcon />}
                  onClick={() => setIsEditModalOpen(true)}
                >
                  <FormattedMessage id="organizations.create.addCities" />
                </Button>
              </Box>
            </Box>

            {assignedCities.length === 0 ? (
              <Box sx={{ p: 12, textAlign: 'center' }}>
                <LocationOnIcon
                  sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }}
                />
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  <FormattedMessage id="organizations.create.noCities" />
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  <FormattedMessage id="organizations.create.noCitiesHint" />
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {cityVisibleColumns.map((column) => (
                          <TableCell key={column.id}>
                            {column.sortable !== false ? (
                              <TableSortLabel
                                active={citySortBy === column.id}
                                direction={
                                  citySortBy === column.id
                                    ? citySortDirection
                                    : 'asc'
                                }
                                onClick={() => handleCitySort(column.id)}
                              >
                                {column.label}
                              </TableSortLabel>
                            ) : (
                              column.label
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedCities.map((c) => (
                        <TableRow key={c.id} hover>
                          {cityVisibleColumns.map((column) => {
                            if (column.id === 'name')
                              return (
                                <TableCell key={column.id}>{c.name}</TableCell>
                              )
                            if (column.id === 'state')
                              return (
                                <TableCell key={column.id}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {c.state}
                                  </Typography>
                                </TableCell>
                              )
                            if (column.id === 'country')
                              return (
                                <TableCell key={column.id}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {c.country}
                                  </Typography>
                                </TableCell>
                              )
                            return null
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TableToolbar
                  page={cityPage}
                  rowsPerPage={cityRowsPerPage}
                  totalCount={assignedCitiesData.length}
                  onChangePage={handleCityChangePage}
                  onChangeRowsPerPage={handleCityChangeRowsPerPage}
                />
              </>
            )}
          </Card>

          <CitySelectionDialog
            open={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            onSave={handleSaveCities}
            cities={cities}
            cityGroups={cityGroups}
            initialSelection={assignedCities}
            title={intl.formatMessage({ id: 'organizations.create.addCities' })}
          />
        </Box>
      </ThemeProvider>
    </CacheProvider>
  )
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-call
export default withRouter(OrganizationCreate)
